import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Check,
  ChevronRight,
  ArrowLeft,
  Phone,
  ShieldCheck,
  Clock,
  BadgeCheck,
  Stethoscope,
  HeartHandshake,
  Activity,
  Baby,
  Dumbbell,
  HeartPulse,
  BriefcaseMedical,
  TestTubes,
  Syringe,
  Cog,
  BedDouble,
  Cable,
  Bandage,
  Footprints,
  PersonStanding,
  Brain,
  Users,
} from "lucide-react";
import { Quote, Star } from "lucide-react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import BookingForm from "./BookingForm";
import useScrollReveal from "../hooks/useScrollReveal";
import { SERVICES } from "../mock";
import { SEO_CONTENT } from "../seoContent";
import { useServiceSeo, SeoSections, SeoFaqs } from "./ServiceSeo";

const ICONS = {
  Stethoscope,
  HeartHandshake,
  Activity,
  Baby,
  Dumbbell,
  HeartPulse,
  BriefcaseMedical,
  TestTubes,
  Syringe,
  Cog,
  BedDouble,
  Cable,
  Bandage,
  Footprints,
  PersonStanding,
  Brain,
  ShieldCheck,
  Users,
};

const TRUST = [
  { icon: ShieldCheck, label: "Verified & trained professionals" },
  { icon: Clock, label: "Available 24/7 across 6 cities" },
  { icon: BadgeCheck, label: "Dedicated care manager" },
];

const TESTIMONIALS = [
  { name: "Ananya Iyer", city: "Bengaluru", quote: "The care felt personal from the very first visit. Our attendant was gentle, punctual and truly treated my mother like family. I finally have peace of mind." },
  { name: "Rajiv Malhotra", city: "Hyderabad", quote: "Impeccable professionalism. The team was discreet, warm and beautifully coordinated — exactly the standard of care our family expects at home." },
  { name: "Meera Nair", city: "Chennai", quote: "What impressed me most was the compassion behind the clinical expertise. My father recovered comfortably at home, surrounded by people who genuinely cared." },
  { name: "Vikram Reddy", city: "Kolkata", quote: "Seamless from booking to bedside. The dedicated care manager kept us informed every step of the way. A truly premium, worry-free experience." },
  { name: "Priya Deshpande", city: "Kochi", quote: "Reliable, kind and highly skilled. They arrived on time, every time, and treated our home with the utmost respect. I recommend them without hesitation." },
  { name: "Arjun Menon", city: "Coimbatore", quote: "The attention to detail was remarkable. From hygiene to companionship, everything was handled with grace. My grandmother adores her caregiver." },
  { name: "Kavita Sharma", city: "Bengaluru", quote: "Finally, healthcare at home that feels effortless and elegant. Responsive coordination, trained staff and genuine warmth. Worth every rupee." },
  { name: "Suresh Rao", city: "Hyderabad", quote: "They turned a stressful recovery into a calm, dignified experience for my wife. Professional, empathetic and always a phone call away." },
  { name: "Neha Kulkarni", city: "Chennai", quote: "A wonderful blend of clinical precision and human kindness. Our nurse was patient and attentive, and communication with the family was excellent." },
  { name: "Aditya Verma", city: "Kolkata", quote: "Trustworthy and thoroughly vetted staff — that mattered most to us. My parents felt safe and genuinely cared for throughout." },
  { name: "Lakshmi Subramanian", city: "Kochi", quote: "The quality of care rivals the finest hospitals, but with the comfort of home. Thoughtful, prompt and deeply reassuring." },
  { name: "Rohan Gupta", city: "Coimbatore", quote: "Everything was handled with quiet efficiency and real compassion. I never once had to follow up — the team was always a step ahead." },
  { name: "Sneha Patil", city: "Bengaluru", quote: "From the first call, I felt heard and supported. The caregiver was skilled, cheerful and wonderful company for my mother." },
  { name: "Karthik Krishnan", city: "Hyderabad", quote: "Exceptional service and genuine care. They anticipated our needs and delivered comfort and dignity to my father's daily routine." },
  { name: "Divya Menon", city: "Chennai", quote: "Warm, dependable and beautifully organised. Our family finally rests easy knowing my grandfather is in such capable hands." },
  { name: "Manish Agarwal", city: "Kolkata", quote: "A five-star experience in every sense. Compassionate professionals, transparent updates and a level of care that truly stands apart." },
];

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const seo = SEO_CONTENT[slug];
  useServiceSeo(seo);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  useScrollReveal([slug]);

  if (!service) {
    return (
      <>
        <Navbar />
        <div className="max-w-3xl mx-auto px-5 pt-40 pb-32 text-center">
          <h1 className="font-heading font-extrabold text-3xl text-[#1B2A4A]">
            Service not found
          </h1>
          <Link
            to="/"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-[#1B2A4A] text-white font-semibold"
          >
            <ArrowLeft size={18} /> Back to Home
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const Icon = ICONS[service.icon] || Stethoscope;

  const svcIndex = Math.max(0, SERVICES.findIndex((s) => s.slug === service.slug));
  const reviews = [
    TESTIMONIALS[(svcIndex * 2) % TESTIMONIALS.length],
    TESTIMONIALS[(svcIndex * 2 + 1) % TESTIMONIALS.length],
  ];

  return (
    <>
      <Navbar />

      {/* Hero */}
      <section
        className="pt-[118px] pb-14 md:pb-20 px-5 md:px-8"
        style={{
          background:
            "radial-gradient(1000px 500px at 88% 0%, hsl(var(--primary)/0.10) 0%, hsl(var(--primary)/0) 55%), hsl(var(--background))",
        }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[13px] text-[#8b96a3] mb-7">
            <Link to="/" className="hover:text-[#159A90] transition-colors">Home</Link>
            <ChevronRight size={14} />
            <Link to="/#services" className="hover:text-[#159A90] transition-colors">Services</Link>
            <ChevronRight size={14} />
            <span className="text-[#1B2A4A] font-medium">{service.title}</span>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className="fade-up">
              <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#159A90]/10 mb-5">
                <Icon size={26} className="text-[#159A90]" />
              </span>
              <h1 className="font-heading font-extrabold text-[#1B2A4A] text-[38px] md:text-[50px] leading-[1.05] tracking-tight" data-testid="service-h1">
                {seo ? seo.h1 : service.title}
              </h1>
              <span className="block w-14 h-[3px] rounded-full bg-[#159A90] mt-4 mb-5" />
              <p className="text-[#57616e] text-[16px] md:text-[17px] leading-relaxed max-w-xl">
                {service.desc}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-8">
                <a
                  href="#book-service"
                  className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#159A90] text-white font-semibold hover:bg-[#0E7A72] transition-colors shadow-[0_10px_30px_rgba(21,154,144,0.35)]"
                >
                  <Phone size={18} /> Book this Service
                </a>
                <Link
                  to="/#services"
                  className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white border border-[#DEE6EE] text-[#1B2A4A] font-semibold hover:border-[#159A90] hover:text-[#159A90] transition-colors"
                >
                  <ArrowLeft size={18} /> All Services
                </Link>
              </div>
            </div>

            <div className="fade-up">
              <div className="rounded-[26px] overflow-hidden shadow-[0_24px_60px_rgba(0,0,0,0.14)]">
                <img
                  src={service.detailImg || service.img}
                  alt={service.title}
                  className="w-full h-[300px] md:h-[440px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="py-16 md:py-20 px-5 md:px-8">
        <div className="max-w-4xl mx-auto">
          {seo ? <SeoSections seo={seo} /> : (
          <div className="fade-up">
            <span className="text-[12px] font-bold tracking-[0.16em] text-[#159A90] uppercase">
              About the Service
            </span>
            <h2 className="font-heading font-extrabold text-[#1B2A4A] text-[26px] md:text-[32px] leading-tight mt-3">
              Care that feels like family, backed by clinical rigour
            </h2>
            <p className="text-[#57616e] text-[16px] leading-relaxed mt-5">
              {service.about}
            </p>

            {/* Trust strip */}
            <div className="mt-8 grid sm:grid-cols-3 gap-3">
              {TRUST.map((t) => (
                <div key={t.label} className="flex items-center gap-3 bg-[#E7EDF3] rounded-2xl p-4">
                  <t.icon size={20} className="text-[#159A90] shrink-0" />
                  <span className="text-[13px] text-[#1B2A4A] font-medium leading-snug">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
          )}
        </div>
      </section>

      {/* Key Components (e.g. ICU at Home) */}
      {service.keyComponents && (
        <section className="pb-4 md:pb-8 px-5 md:px-8">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)] gap-10 lg:gap-14 items-start">
            <div className="fade-up lg:sticky lg:top-28">
              <h2 className="font-heading font-extrabold text-[#1B2A4A] text-[28px] md:text-[36px] leading-tight">
                Key Components of {service.title}
              </h2>
              {service.keyComponentsIntro && (
                <p className="text-[#57616e] text-[16px] leading-relaxed mt-5">
                  {service.keyComponentsIntro}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 fade-up">
              {service.keyComponents.map((c) => {
                const CIcon = ICONS[c.icon] || Check;
                return (
                  <div
                    key={c.label}
                    className="group bg-white rounded-2xl border border-[#DEE6EE] p-5 flex flex-col items-center text-center hover:shadow-[0_16px_40px_rgba(0,0,0,0.08)] hover:-translate-y-1 transition-all"
                  >
                    <span className="flex items-center justify-center w-14 h-14 rounded-2xl bg-[#159A90]/10 mb-3 group-hover:bg-[#159A90]/15 transition-colors">
                      <CIcon size={26} className="text-[#159A90]" strokeWidth={1.6} />
                    </span>
                    <span className="font-heading font-semibold text-[14px] text-[#1B2A4A] leading-snug">
                      {c.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Booking form (prefilled with this service) */}
      <div id="book-service">
        <BookingForm defaultService={service.title} />
      </div>

      {/* Testimonials */}
      <section className="pt-14 md:pt-16 pb-4 md:pb-6 px-5 md:px-8" data-testid="service-testimonials">
        <div className="max-w-5xl mx-auto">
          <div className="text-center fade-up">
            <span className="text-[12px] font-bold tracking-[0.16em] text-[#159A90] uppercase">
              Loved by families
            </span>
            <h2 className="font-heading font-extrabold text-[#1B2A4A] text-[24px] md:text-[32px] leading-tight mt-3">
              What our families say
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-5 md:gap-6 mt-9">
            {reviews.map((r) => (
              <div
                key={r.name}
                data-testid={`testimonial-${r.name.toLowerCase().replace(/\s+/g, "-")}`}
                className="relative bg-white rounded-[22px] border border-[#DEE6EE] p-7 shadow-[0_10px_30px_rgba(0,0,0,0.05)] fade-up"
              >
                <Quote size={30} className="text-[#159A90]/25 absolute top-6 right-6" />
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-[#F0A93B] fill-[#F0A93B]" />
                  ))}
                </div>
                <p className="text-[#3a4757] text-[15.5px] leading-relaxed">
                  &ldquo;{r.quote}&rdquo;
                </p>
                <div className="flex items-center gap-3 mt-6">
                  <span className="flex items-center justify-center w-11 h-11 rounded-full bg-[#1B2A4A] text-white font-heading font-bold text-[15px] shrink-0">
                    {r.name.split(" ").map((n) => n[0]).join("")}
                  </span>
                  <div>
                    <p className="font-heading font-bold text-[15px] text-[#1B2A4A] leading-tight">
                      {r.name}
                    </p>
                    <p className="text-[13px] text-[#8b96a3]">{r.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {seo && <SeoFaqs faqs={seo.faqs} />}

      {/* Other services */}
      <section className="pb-20 md:pb-28 px-5 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading font-extrabold text-[#1B2A4A] text-[24px] md:text-[30px] text-center">
            Explore other services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-9">
            {SERVICES.filter((s) => s.slug !== service.slug)
              .slice(0, 4)
              .map((s) => {
                const SIcon = ICONS[s.icon] || Stethoscope;
                return (
                  <Link
                    key={s.slug}
                    to={`/services/${s.slug}`}
                    className="group bg-white rounded-2xl p-5 border border-[#DEE6EE] shadow-[0_4px_16px_rgba(0,0,0,0.03)] hover:shadow-[0_16px_40px_rgba(0,0,0,0.1)] hover:-translate-y-1 transition-all"
                  >
                    <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#159A90]/10 mb-3">
                      <SIcon size={20} className="text-[#159A90]" />
                    </span>
                    <h3 className="font-heading font-bold text-[15px] text-[#1B2A4A] leading-snug">
                      {s.title}
                    </h3>
                    <span className="inline-flex items-center gap-1 mt-2 text-[13px] font-semibold text-[#159A90] group-hover:gap-2 transition-all">
                      View <ChevronRight size={14} />
                    </span>
                  </Link>
                );
              })}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ServiceDetail;
